import * as Hogan from 'hogan.js';
import {SCALE_MODES, Sprite, Loader} from 'pixi.js';
import {
    consoleLogger,
    CoreDI, DisplayObjectUtils,
    inject,
    injectable,
    IService,
    ScreenManager,
    ResourceLoader,
    System,
    SystemComponentType
} from '@gongxigames/framework/core';
import {DI} from '@gongxigames/framework/slot/configs/DI';

export type ImagesMap = Record<string, string>;

export interface BasePopupObject {
    popup: HTMLDivElement;
    parent: HTMLDivElement;
    priority?: number;
}

export interface PopupObject extends BasePopupObject {
    type?: number;
    header: HTMLDivElement;
    closeButton: HTMLDivElement;
    content: HTMLDivElement;
    id?: number;
}

@injectable()
export class TemplateUtils implements IService {
    public static NAME: symbol = DI.TemplateUtils;
    public type: SystemComponentType = 'service';

    @inject(CoreDI.System)
    private _system!: System;

    @inject(CoreDI.ResourceLoader)
    private _loader!: ResourceLoader;

    @inject(CoreDI.ScreenManager)
    private _screenManager!: ScreenManager;

    @inject(CoreDI.DisplayObjectUtils)
    private _displayObjectUtils!: DisplayObjectUtils;

    public static createPopup(id: string, header: string, templateData: string = Loader.shared.resources.popup.data): PopupObject {
        const template = Hogan.compile(templateData);
        const html = template.render({
            id,
            header
        });

        document.body.insertAdjacentHTML('beforeend', html);

        const popup = document.getElementById(id) as HTMLDivElement;

        return {
            type: 2,
            popup,
            parent: popup.parentElement as HTMLDivElement,
            header: document.getElementById(`${id}-header`) as HTMLDivElement,
            closeButton: document.getElementById(`${id}-close`) as HTMLDivElement,
            content: document.getElementById(`${id}-content`) as HTMLDivElement,
            priority: 0
        };
    }

    public static createHTMLElement(name: string, id: string, templateParams: any = {}): BasePopupObject {
        const template = Hogan.compile(Loader.shared.resources[name].data);

        templateParams.id = id;

        const html = template.render(templateParams);

        document.body.insertAdjacentHTML('beforeend', html);
        const popup = document.getElementById(id) as HTMLDivElement;

        return {
            popup,
            parent: popup.parentElement as HTMLDivElement,
            priority: 0
        };
    }

    public static createHTMLDivElementByTemplate(name: string, params: any = {}): HTMLDivElement {
        const div: HTMLDivElement = document.createElement('div');
        const html: string = TemplateUtils.renderTemplate(name, params);

        div.insertAdjacentHTML('beforeend', html);

        return div;
    }

    public static renderTemplate(name: string, templateParams: any = {}): string {
        if (!Loader.shared.resources[name]) {
            consoleLogger.error(`Can't find template: ${name}`);

            return '';
        }

        const template = Hogan.compile(Loader.shared.resources[name].data);

        return template.render(templateParams);
    }

    /**
     * Find keys like {{images.imageName}} and return array of imageNames
     *
     * @param {string} str template string
     * @returns {string[]} array of image names
     */
    public getImageKeys(str: string): string[] {
        const keys = str.match(/{{images.+?(?=})/g);

        if (!keys) {
            return [];
        }

        return keys.map((val) => {
            return val.replace('{{images.', '');
        });
    }

    /**
     * Create object with key - image name and value - base64 image
     *
     * @param {string} str template string
     * @param {string} language localization
     * @returns {Object}
     */
    public createImageMap(str: string, language = 'en', customResolution?: number): Record<string, string> {
        const images = this.getImageKeys(str);
        const img: Record<string, string> = {};
        const resolution = customResolution ?? this._screenManager.defaultViewportResolution;

        for (const image of images) {
            const replacedName = image.replace(/_en$/g, `_${language}`);
            const textureName = this._loader.getTexture(replacedName) ? replacedName : image;

            // already exist
            if (img[image]) {
                continue;
            }

            if (!this._loader.getTexture(textureName)) {
                consoleLogger.error(`Can't find texture: ${textureName}`);

                continue;
            }

            const sprite = new Sprite(this._loader.getTexture(textureName));
            const texture = this._system.renderer.generateTexture(sprite, {
                scaleMode: SCALE_MODES.LINEAR,
                resolution,
            });

            img[image] = this._system.renderer.plugins.extract.base64(texture);
        }

        return img;
    }

    public createImageMapAsync(str: string, language = 'en'): ImagesMap {
        const images = this.getImageKeys(str);
        const img: Record<string, string> = {};

        for (const image of images) {
            const replacedName = image.replace(/_en$/g, `_${language}`);
            const textureName = this._loader.getTexture(replacedName) ? replacedName : image;

            // already exist
            if (img[image]) {
                continue;
            }

            const texture = this._loader.getTexture(textureName);

            if (!texture) {
                consoleLogger.error(`Can't find texture: ${textureName}`);

                continue;
            }

            img[image] = this._displayObjectUtils.textureToBase64(texture);
        }

        return img;
    }

    public getAnimationKeys(str: string): string[] {
        const keys = str.match(/animations.+?(?=")/g);

        if (!keys) {
            return [];
        }

        return keys.map((val) => {
            return val.replace('animations.', '');
        });
    }
}
